import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import type { PrivatePlayerSnapshot, PublicRoomSnapshot } from '@sincronia/shared';
import { TopBar } from './TopBar.js';
import { PlayerChips } from './PlayerChips.js';
import { Board } from './Board.js';
import { Hand } from './Hand.js';
import { RemoteDrags } from './RemoteDrags.js';
import { Overlays, type OverlayActions } from './Overlays.js';
import { Button } from '../ui.js';
import { useT } from '../../i18n/index.js';
import { emitAck, emitFast } from '../../net/socket.js';
import { useGameStore } from '../../store/gameStore.js';
import { playSound, vibrate } from '../../lib/feedback.js';
import { clearSession } from '../../lib/session.js';

/** Pausa antes de mostrar o resumo do nível, para todos verem a jogada final. */
const FINAL_REVEAL_HOLD_MS = 3000;

export function GameView({
  snapshot,
  privateState,
  isHost,
}: {
  snapshot: PublicRoomSnapshot;
  privateState: PrivatePlayerSnapshot | null;
  isHost: boolean;
}): ReactNode {
  const t = useT();
  const navigate = useNavigate();
  const reset = useGameStore((state) => state.reset);
  const stageRef = useRef<HTMLDivElement>(null);
  const dropRef = useRef<HTMLDivElement>(null);
  const [dragState, setDragState] = useState({ active: false, overDrop: false });

  const pendingCardId = useGameStore((state) => state.pendingCardId);
  const setPendingCard = useGameStore((state) => state.setPendingCard);
  const pushToast = useGameStore((state) => state.pushToast);
  const flash = useGameStore((state) => state.flash);

  const soundEnabled = snapshot.config.soundEnabled;
  const vibrationEnabled = snapshot.config.vibrationEnabled;

  /*
   * A última carta do nível encerrava a fase no mesmo instante e o resumo
   * cobria o tabuleiro: ninguém via a jogada final. Aqui seguramos o resumo
   * por um momento para que todos vejam a carta e o nome de quem a jogou.
   */
  const [holdingFinalReveal, setHoldingFinalReveal] = useState(false);
  const previousPhase = useRef(snapshot.phase);

  useEffect(() => {
    const before = previousPhase.current;
    previousPhase.current = snapshot.phase;

    const acabou = snapshot.phase === 'LEVEL_COMPLETE' || snapshot.phase === 'GAME_WON';
    // Só seguramos quando o nível terminou por uma carta jogada. Vindo de
    // RESOLVING_ERROR ou RESOLVING_STAR, aquelas telas já revelaram o que
    // aconteceu e uma pausa extra seria arrastada.
    if (!acabou || before !== 'PLAYING') return;

    setHoldingFinalReveal(true);
    const timer = window.setTimeout(() => setHoldingFinalReveal(false), FINAL_REVEAL_HOLD_MS);
    return () => window.clearTimeout(timer);
  }, [snapshot.phase]);

  /* Retorno tátil e sonoro dos eventos importantes. */
  const lastFlashAt = useRef(0);
  useEffect(() => {
    if (!flash || flash.at === lastFlashAt.current) return;
    lastFlashAt.current = flash.at;

    if (flash.kind === 'accepted') {
      playSound(flash.payload.causedError ? 'error' : 'play', soundEnabled);
      vibrate(flash.payload.causedError ? 'error' : 'play', vibrationEnabled);
    } else if (flash.kind === 'error') {
      playSound('error', soundEnabled);
      vibrate('error', vibrationEnabled);
    } else if (flash.kind === 'reward') {
      playSound('reward', soundEnabled);
    } else if (flash.kind === 'star' && flash.approved) {
      playSound('star', soundEnabled);
    }
  }, [flash, soundEnabled, vibrationEnabled]);

  useEffect(() => {
    if (snapshot.phase === 'COUNTDOWN') vibrate('countdown', vibrationEnabled);
    if (snapshot.phase === 'GAME_WON') {
      playSound('win', soundEnabled);
      vibrate('win', vibrationEnabled);
    }
  }, [snapshot.phase, soundEnabled, vibrationEnabled]);

  const handlePlay = useCallback(
    (cardId: string) => {
      setPendingCard(cardId);
      void emitAck<null>('card:play', { cardId }).then((response) => {
        if (!response.ok) setPendingCard(null);
      });
    },
    [setPendingCard],
  );

  const actions: OverlayActions = {
    setReady: (ready) => {
      void emitAck<null>('player:ready', { ready });
    },
    voteStar: (approve) => {
      void emitAck<null>('star:vote', { approve });
    },
    playAgain: () => {
      void emitAck<null>('game:playAgain', {});
    },
    endGame: () => {
      void emitAck<null>('room:endGame', {});
    },
    close: () => {
      // A partida já acabou: saímos da sala e voltamos para o início.
      void emitAck<null>('room:leave', {}).then(() => {
        clearSession(snapshot.code);
        reset();
        void navigate('/');
      });
    },
  };

  const requestRefocus = (): void => {
    void emitAck<null>('player:requestRefocus', {}).then((response) => {
      if (!response.ok) pushToast(response.error.code);
    });
  };

  const requestStar = (): void => {
    void emitAck<null>('star:request', {}).then((response) => {
      if (!response.ok) pushToast(response.error.code);
    });
  };

  useEffect(() => {
    if (snapshot.phase === 'PLAYING') return;
    // Nenhum arrasto sobrevive a uma mudança de fase.
    if (dragState.active) emitFast('drag:cancel', {});
  }, [snapshot.phase, dragState.active]);

  const canPlay = snapshot.phase === 'PLAYING';
  const meId = privateState?.playerId ?? null;
  const showReady = ['READY', 'REFOCUS', 'LEVEL_COMPLETE'].includes(snapshot.phase);

  return (
    <div className="flex h-dvh flex-col overflow-hidden">
      <TopBar snapshot={snapshot} />
      <PlayerChips players={snapshot.players} meId={meId} showReady={showReady} />

      <div ref={stageRef} className="relative flex min-h-0 flex-1 flex-col">
        <Board
          snapshot={snapshot}
          dropRef={dropRef}
          overDrop={dragState.overDrop}
          pending={Boolean(pendingCardId)}
          flash={flash}
        />

        <div className="safe-bottom flex flex-col gap-2 pb-1">
          <Hand
            hand={privateState?.hand ?? []}
            playableCardId={privateState?.playableCardId ?? null}
            canPlay={canPlay}
            pending={Boolean(pendingCardId)}
            stageRef={stageRef}
            dropRef={dropRef}
            onDragChange={setDragState}
            onPlay={handlePlay}
          />

          <div className="safe-x flex items-center justify-center gap-2 px-3">
            <Button
              variant="secondary"
              className="min-h-11 flex-1 px-3 py-2 text-sm"
              disabled={!canPlay}
              onClick={requestRefocus}
              aria-label={t('game.refocus')}
              data-testid="refocus-button"
            >
              {t('game.refocusShort')}
            </Button>
            <Button
              variant="secondary"
              className="min-h-11 flex-1 px-3 py-2 text-sm"
              disabled={!canPlay || snapshot.stars <= 0}
              onClick={requestStar}
              data-testid="star-button"
            >
              {snapshot.stars > 0 ? t('game.star') : t('star.none')}
            </Button>
          </div>
        </div>

        <RemoteDrags stageRef={stageRef} players={snapshot.players} meId={meId} />
      </div>

      {holdingFinalReveal ? null : (
        <Overlays
          snapshot={snapshot}
          privateState={privateState}
          isHost={isHost}
          actions={actions}
        />
      )}
    </div>
  );
}
